import { SEVERITIES, type LogQuery, type Severity } from '../api/logs';

/** Relative ranges; `custom` uses `from`/`to`. */
export const RANGES = ['15m', '1h', '4h', '12h', '24h', '7d', 'custom'] as const;
export type RangeKey = (typeof RANGES)[number];
const RANGE_MS: Record<Exclude<RangeKey, 'custom'>, number> = {
  '15m': 15 * 60_000,
  '1h': 3_600_000,
  '4h': 4 * 3_600_000,
  '12h': 12 * 3_600_000,
  '24h': 24 * 3_600_000,
  '7d': 7 * 86_400_000,
};

export const PAGE_SIZES = [100, 200, 500, 1000];
export const DEFAULT_PAGE_SIZE = 200;
export const DEFAULT_RANGE: RangeKey = '1h';

/** WARNING and worse. */
export const ERRORS_ONLY: Severity[] = ['WARNING', 'ERROR', 'CRITICAL', 'ALERT', 'EMERGENCY'];

/**
 * Everything the Logs page filters on, as held in the URL (shareable links). Empty `namespaces` / `severities` mean
 * "all"; `from`/`to` are `datetime-local` values (local time, minutes) used only with `range=custom`.
 */
export interface LogFilters {
  env: string;
  namespaces: string[];
  pod: string;
  container: string;
  text: string;
  severities: Severity[];
  range: RangeKey;
  from: string;
  to: string;
  pageSize: number;
}

export const EMPTY_FILTERS: LogFilters = {
  env: '',
  namespaces: [],
  pod: '',
  container: '',
  text: '',
  severities: [],
  range: DEFAULT_RANGE,
  from: '',
  to: '',
  pageSize: DEFAULT_PAGE_SIZE,
};

const list = (v: string | null) =>
  (v ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

export function parseLogParams(p: URLSearchParams): LogFilters {
  const range = p.get('range') as RangeKey | null;
  const size = Number(p.get('size'));
  return {
    env: p.get('env') ?? '',
    namespaces: list(p.get('ns')),
    pod: p.get('pod') ?? '',
    container: p.get('container') ?? '',
    text: p.get('q') ?? '',
    severities: list(p.get('sev'))
      .map((s) => s.toUpperCase())
      .filter((s): s is Severity => (SEVERITIES as readonly string[]).includes(s)),
    range: range && (RANGES as readonly string[]).includes(range) ? range : DEFAULT_RANGE,
    from: p.get('from') ?? '',
    to: p.get('to') ?? '',
    pageSize: PAGE_SIZES.includes(size) ? size : DEFAULT_PAGE_SIZE,
  };
}

/** Only non-default values go in the URL, in a fixed order. */
export function filtersToParams(f: LogFilters): URLSearchParams {
  const p = new URLSearchParams();
  const set = (k: string, v: string) => v && p.set(k, v);
  set('env', f.env);
  set('ns', f.namespaces.join(','));
  set('pod', f.pod);
  set('container', f.container);
  set('q', f.text);
  set('sev', f.severities.join(','));
  if (f.range !== DEFAULT_RANGE) p.set('range', f.range);
  if (f.range === 'custom') {
    set('from', f.from);
    set('to', f.to);
  }
  if (f.pageSize !== DEFAULT_PAGE_SIZE) p.set('size', String(f.pageSize));
  return p;
}

/** A `datetime-local` value (`2026-09-30T10:15`) for a Date, in local time. */
export function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The time window of the filters, ISO UTC; null when a custom range is incomplete or inverted. */
export function timeWindow(f: LogFilters, now: number = Date.now()): { from: string; to: string } | null {
  if (f.range === 'custom') {
    const from = new Date(f.from).getTime();
    const to = f.to ? new Date(f.to).getTime() : now;
    if (!Number.isFinite(from) || !Number.isFinite(to) || from >= to) return null;
    return { from: new Date(from).toISOString(), to: new Date(to).toISOString() };
  }
  return { from: new Date(now - RANGE_MS[f.range]).toISOString(), to: new Date(now).toISOString() };
}

/**
 * The API query: namespaces/severities are omitted when all are selected (the server then uses all of the
 * environment's namespaces / every severity); `allNamespaces` are the environment's.
 */
export function buildLogQuery(
  f: LogFilters,
  allNamespaces: string[],
  now: number = Date.now()
): LogQuery | null {
  const window = timeWindow(f, now);
  if (!f.env || !window) return null;
  const ns = f.namespaces.filter((n) => allNamespaces.includes(n));
  const q: LogQuery = { environmentId: f.env, from: window.from, to: window.to, pageSize: f.pageSize };
  if (ns.length && ns.length < allNamespaces.length) q.namespaces = ns;
  if (f.pod.trim()) q.pod = f.pod.trim();
  if (f.container.trim()) q.container = f.container.trim();
  if (f.text.trim()) q.text = f.text.trim();
  if (f.severities.length && f.severities.length < SEVERITIES.length) q.severities = f.severities;
  return q;
}

/** Exact-match RE2 regex for a pod name (Pods page → Logs link). */
export const exactPodRegex = (name: string) => `^${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`;

/**
 * A `/logs` link with the environment and, optionally, namespace/pod/container prefilled. With `since`, the range is
 * custom from that moment (rounded down to the minute) to now — used by the pod log drawer so Cloud Logging opens on
 * the same window the drawer showed; without it the default range applies.
 */
export function logsLink(
  env: string,
  namespace?: string,
  pod?: string,
  opts: { container?: string; since?: Date; now?: Date } = {}
): string {
  const custom = opts.since && Number.isFinite(opts.since.getTime());
  const p = filtersToParams({
    ...EMPTY_FILTERS,
    env,
    namespaces: namespace ? [namespace] : [],
    pod: pod ? exactPodRegex(pod) : '',
    container: opts.container ?? '',
    ...(custom
      ? {
          range: 'custom' as const,
          from: toLocalInput(new Date(Math.floor(opts.since!.getTime() / 60_000) * 60_000)),
          to: toLocalInput(new Date(Math.ceil((opts.now ?? new Date()).getTime() / 60_000) * 60_000)),
        }
      : {}),
  });
  return `/logs?${p.toString()}`;
}

/** Severity families for colour (chips, histogram). */
export type SeverityGroup = 'debug' | 'info' | 'warning' | 'error' | 'critical';
export function severityGroup(s: string): SeverityGroup {
  switch (s) {
    case 'INFO':
    case 'NOTICE':
      return 'info';
    case 'WARNING':
      return 'warning';
    case 'ERROR':
      return 'error';
    case 'CRITICAL':
    case 'ALERT':
    case 'EMERGENCY':
      return 'critical';
    default:
      return 'debug';
  }
}
export const SEVERITY_GROUPS: SeverityGroup[] = ['debug', 'info', 'warning', 'error', 'critical'];
